// src/services/dbService.js

const DB_NAME = 'mantraz-ship-db';
const STORE_NAME = 'ship-visibility-status';
const DB_VERSION = 1;

let db = null; // Variabel untuk menyimpan koneksi database agar tidak perlu membuka berulang kali

/**
 * Membuka koneksi ke IndexedDB. Dibuat sebagai Promise agar mudah digunakan dengan async/await.
 * @returns {Promise<IDBDatabase>}
 */
const initDB = () => new Promise((resolve, reject) => {
  // Jika koneksi sudah ada, langsung gunakan
  if (db) {
    return resolve(db);
  }

  // 1. Minta untuk membuka database
  const request = window.indexedDB.open(DB_NAME, DB_VERSION);

  // 2. Tangani error saat membuka DB
  request.onerror = (event) => {
    console.error('Error opening IndexedDB:', event.target.error);
    // eslint-disable-next-line prefer-promise-reject-errors
    reject('Error opening IndexedDB');
  };

  // 3. Tangani saat database perlu di-upgrade (dibuat pertama kali atau versi naik)
  request.onupgradeneeded = (event) => {
    const tempDb = event.target.result;
    // Buat object store (seperti tabel di SQL) jika belum ada
    if (!tempDb.objectStoreNames.contains(STORE_NAME)) {
      tempDb.createObjectStore(STORE_NAME, { keyPath: 'massId' });
    }
  };

  // 4. Tangani saat database berhasil dibuka
  request.onsuccess = (event) => {
    // Simpan koneksi ke variabel global
    db = event.target.result;
    resolve(db);
  };
});

/**
 * Menyimpan status visibilitas untuk satu kapal.
 * @param {string} massId - ID unik kapal
 * @param {boolean} isVisible - Status visibilitas
 * @returns {Promise<void>}
 */
export const saveVisibilityStatus = async (massId, isVisible) => {
  const dbInstance = await initDB();
  return new Promise((resolve, reject) => {
    // Memulai transaksi dengan mode 'readwrite'
    const transaction = dbInstance.transaction(STORE_NAME, 'readwrite');
    const store = transaction.objectStore(STORE_NAME);

    // Menggunakan put() untuk insert atau update data
    store.put({ massId, isVisible });

    // Tangani saat transaksi selesai
    transaction.oncomplete = () => {
      resolve();
    };

    // Tangani jika ada error pada transaksi
    transaction.onerror = (event) => {
      console.error('Error saving visibility status:', event.target.error);
      // eslint-disable-next-line prefer-promise-reject-errors
      reject('Transaction error on save');
    };
  });
};

/**
 * Menyimpan status visibilitas untuk semua kapal sekaligus dalam satu transaksi.
 * @param {Array<{massId: string, isVisible: boolean}>} statuses - Array objek status
 * @returns {Promise<void>}
 */
export const saveAllVisibilityStatuses = async (statuses) => {
  const dbInstance = await initDB();
  return new Promise((resolve, reject) => {
    const transaction = dbInstance.transaction(STORE_NAME, 'readwrite');
    const store = transaction.objectStore(STORE_NAME);

    // Loop melalui semua status dan tambahkan ke object store
    statuses.forEach((status) => {
      store.put(status);
    });

    transaction.oncomplete = () => {
      resolve();
    };

    transaction.onerror = (event) => {
      console.error('Error saving all statuses:', event.target.error);
      // eslint-disable-next-line prefer-promise-reject-errors
      reject('Transaction error on save all');
    };
  });
};

/**
 * Mengambil semua status visibilitas yang tersimpan dari IndexedDB.
 * @returns {Promise<Map<string, boolean>>} - Sebuah Map dengan massId sebagai key dan isVisible sebagai value.
 */
export const getAllVisibilityStatuses = async () => {
  const dbInstance = await initDB();
  return new Promise((resolve, reject) => {
    const transaction = dbInstance.transaction(STORE_NAME, 'readonly');
    const store = transaction.objectStore(STORE_NAME);
    const request = store.getAll();

    request.onerror = (event) => {
      console.error('Error fetching all statuses:', event.target.error);
      // eslint-disable-next-line prefer-promise-reject-errors
      reject('Error fetching all statuses');
    };

    request.onsuccess = () => {
      const allStatuses = request.result;
      // Ubah array menjadi Map untuk pencarian yang lebih cepat (O(1)) saat rekonsiliasi
      const statusMap = new Map();
      allStatuses.forEach((status) => {
        statusMap.set(status.massId, status.isVisible);
      });
      resolve(statusMap);
    };
  });
};
